import { StopCause } from "../../Types/StopCause";

const STOP_REASON_SEPARATOR = '/';
const DIRECT_STOP_REASON_NODE_SEGMENT = '__direct__';

const splitStopReasonPath = (value?: string): string[] => {
    return (value ?? '')
        .split(STOP_REASON_SEPARATOR)
        .map((segment) => segment.trim())
        .filter(Boolean);
};

export const normalizeStopCodingReason = (reason?: string): string => {
    const normalizedReason = reason?.trim() || 'Unknown';
    const loweredReason = normalizedReason.toLowerCase();

    if (loweredReason === 'other' || loweredReason === 'oklassificerat' || normalizedReason.includes('PI Point not found')) {
        return 'Oklassificerat';
    }

    return normalizedReason;
};

export const getStopReasonSegments = (reason?: string): string[] => {
    return splitStopReasonPath(normalizeStopCodingReason(reason));
};

export const getStopReasonPathSegments = (path?: string): string[] => {
    return splitStopReasonPath(path);
};

export const isDirectStopReasonNodePath = (path?: string): boolean => {
    const segments = getStopReasonPathSegments(path);
    return segments[segments.length - 1] === DIRECT_STOP_REASON_NODE_SEGMENT;
};

export const getStopReasonSelectionPath = (path?: string): string => {
    const segments = getStopReasonPathSegments(path);

    if (segments[segments.length - 1] === DIRECT_STOP_REASON_NODE_SEGMENT) {
        return segments.slice(0, -1).join(STOP_REASON_SEPARATOR);
    }

    return segments.join(STOP_REASON_SEPARATOR);
};

export const getDirectStopReasonNodePath = (reasonPath: string): string => {
    const normalizedPath = getStopReasonSelectionPath(reasonPath);

    if (!normalizedPath) {
        return DIRECT_STOP_REASON_NODE_SEGMENT;
    }

    return `${normalizedPath}${STOP_REASON_SEPARATOR}${DIRECT_STOP_REASON_NODE_SEGMENT}`;
};

export const getStopReasonNodeLabel = (path?: string): string => {
    const selectionSegments = getStopReasonPathSegments(getStopReasonSelectionPath(path));
    return selectionSegments[selectionSegments.length - 1] ?? '';
};

export const getStopReasonPath = (reason?: string): string => {
    return getStopReasonSegments(reason).join(STOP_REASON_SEPARATOR);
};

export const isStopReasonInCascadePath = (reason: string, cascadePath: string): boolean => {
    const normalizedReason = getStopReasonPath(reason);
    const normalizedCascadePath = getStopReasonSelectionPath(cascadePath);

    if (!normalizedCascadePath) {
        return true;
    }

    if (isDirectStopReasonNodePath(cascadePath)) {
        return normalizedReason === normalizedCascadePath;
    }

    return normalizedReason === normalizedCascadePath
        || normalizedReason.startsWith(`${normalizedCascadePath}${STOP_REASON_SEPARATOR}`);
};

export const getStopReasonDrilldownGroupPath = (reason: string, cascadePath = ''): string | null => {
    const reasonSegments = getStopReasonSegments(reason);
    const cascadeSegments = getStopReasonPathSegments(getStopReasonSelectionPath(cascadePath));

    if (reasonSegments.length === 0) {
        return null;
    }

    if (isDirectStopReasonNodePath(cascadePath)) {
        const normalizedReason = reasonSegments.join(STOP_REASON_SEPARATOR);
        const normalizedCascadePath = getStopReasonSelectionPath(cascadePath);

        return normalizedReason === normalizedCascadePath ? getDirectStopReasonNodePath(normalizedCascadePath) : null;
    }

    if (cascadeSegments.length > 0) {
        const matchesCascadePath = cascadeSegments.every((segment, index) => reasonSegments[index] === segment);

        if (!matchesCascadePath || reasonSegments.length < cascadeSegments.length) {
            return null;
        }

        if (reasonSegments.length === cascadeSegments.length) {
            return cascadeSegments.join(STOP_REASON_SEPARATOR);
        }

        return reasonSegments.slice(0, cascadeSegments.length + 1).join(STOP_REASON_SEPARATOR);
    }

    return reasonSegments[0];
};

export const getStopReasonPathLabel = (groupPath: string, cascadePath = '', selfLabel = 'Direct'): string => {
    const groupSegments = getStopReasonPathSegments(getStopReasonSelectionPath(groupPath));
    const cascadeSegments = getStopReasonPathSegments(getStopReasonSelectionPath(cascadePath));

    if (groupSegments.length === 0) {
        return '';
    }

    if (cascadeSegments.length > 0 && groupSegments.length === cascadeSegments.length) {
        return selfLabel;
    }

    return groupSegments[cascadeSegments.length] ?? groupSegments[groupSegments.length - 1];
};

export const getStopReasonBreadcrumbs = (cascadePath: string): string[] => {
    const segments = getStopReasonPathSegments(getStopReasonSelectionPath(cascadePath));

    return segments.map((_, index) => segments.slice(0, index + 1).join(STOP_REASON_SEPARATOR));
};

export const isValidStopCodingDate = (value: Date) => !Number.isNaN(value.getTime());

export const toStopCodingDate = (value: Date | string | undefined): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const parsed = new Date(value);

    return isValidStopCodingDate(parsed) ? parsed : undefined;
};

export const filterStopCausesForCascade = (
    stopCauses: StopCause[],
    startDate: Date | string | undefined,
    endDate: Date | string | undefined
): StopCause[] => {
    const parsedStartDate = toStopCodingDate(startDate);
    const parsedEndDate = toStopCodingDate(endDate);

    return stopCauses.filter((stop) => {
        const stopDate = new Date(stop.StartTime);

        if (!isValidStopCodingDate(stopDate)) {
            return false;
        }

        if (parsedStartDate && stopDate < parsedStartDate) {
            return false;
        }

        if (parsedEndDate && stopDate > parsedEndDate) {
            return false;
        }

        return true;
    });
};